import React from "react";
import {
  Box,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { GlassCard } from "../../FatigueManagement/components/primitives";
import { SOURCES } from "../data/model";

/** Explains the model's stance and lists the cited evidence base. */
export const MethodologySection: React.FC = () => (
  <Stack spacing={3}>
    <Box>
      <Typography variant="h5" fontWeight={800}>
        Methodology & Sources
      </Typography>
      <Typography variant="body2" color="text.secondary">
        How the ROI is derived and what it is based on.
      </Typography>
    </Box>

    <GlassCard>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
        The stance
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        We do not assign a monetary value to a change in a physiological metric.
        Improvements in sleep, HRV, recovery, resting heart rate and strain are{" "}
        <strong>leading indicators</strong> — they show an intervention is
        working. The ROI is modelled against the <strong>business outcomes</strong>{" "}
        those improvements drive: reduced absence and presenteeism, higher
        productivity, fewer injuries, lower turnover and reduced healthcare
        cost.
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Each category is a workforce assumption × a published UK cost benchmark ×
        a targeted reduction. A single <strong>attribution factor</strong> then
        discounts the headline to the share credibly caused by the programme —
        so even at a conservative 10–20% the case stands on its own.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        All inputs are editable on the Assumptions tab; the calculation is fully
        transparent and shown line-by-line in the breakdown.
      </Typography>
    </GlassCard>

    <GlassCard>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
        Evidence base
      </Typography>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small" aria-label="ROI evidence sources">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Area</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Key statistic</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Why it matters</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {SOURCES.map((s) => (
              <TableRow key={s.area} hover>
                <TableCell sx={{ fontWeight: 600 }}>{s.area}</TableCell>
                <TableCell>{s.stat}</TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {s.why}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Link
                    href={s.source}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="caption"
                  >
                    View
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </GlassCard>
  </Stack>
);
